/**
 * @file 資料のページ一覧を「ページ番号 → ページ」の map で引く hook。
 *
 * @what: fetchSourcePages を資料ごとに 1 回だけ呼び (取得中の Promise も共有)、
 *   Record<page, StudyPage> にして返す。sourceId が null なら何もしない。
 * @why: うんどうチェックのサムネイルは「本のページ番号」から画像 path を引く必要があり、
 *   50 行の描画で一覧 JSON を何度も取りに行かないようにする (useSourceCover と同じ方針)。
 * @failure: 取得失敗は null のまま (サムネイルが出ないだけで、チェックは使える)。
 *   失敗はキャッシュに残さないので、開き直せば取り直す。
 */

import { useEffect, useState } from "react";
import { fetchSourcePages, type StudyPage } from "../api/pages";

const pagesCache = new Map<string, Promise<StudyPage[]>>();

export function useSourcePageMap(
  familyId: string,
  sourceId: string | null,
): Readonly<Record<number, StudyPage>> | null {
  const [map, setMap] = useState<Record<number, StudyPage> | null>(null);

  useEffect(() => {
    if (!sourceId) {
      setMap(null);
      return;
    }
    let cancelled = false;
    const key = `${familyId}/${sourceId}`;
    let p = pagesCache.get(key);
    if (!p) {
      p = fetchSourcePages(familyId, sourceId);
      p.catch(() => pagesCache.delete(key));
      pagesCache.set(key, p);
    }
    p.then((pages) => {
      if (cancelled) return;
      const byPage: Record<number, StudyPage> = {};
      for (const page of pages) byPage[page.page] = page;
      setMap(byPage);
    }).catch(() => {
      if (!cancelled) setMap(null);
    });
    return () => {
      cancelled = true;
    };
  }, [familyId, sourceId]);

  return map;
}

/** テスト用。ページ一覧の覚えを空にする。 */
export function resetSourcePageMapCache(): void {
  pagesCache.clear();
}
